import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../Components/PageHeader";
import { UserPlus, X, Search } from "lucide-react";
import AssignNurseModal from "./AssignNurseModal";

const NurseAssignments = () => {
  const [nurses, setNurses] = useState([]);
  const [filteredNurses, setFilteredNurses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState(null);

  useEffect(() => {
    fetchNurseAssignments();
  }, []);

  useEffect(() => {
    filterNurses();
  }, [searchQuery, nurses]);

  const fetchNurseAssignments = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/nurse-assignments");
      setNurses(res.data.nurses || []);
    } catch (error) {
      console.error("Failed to fetch nurse assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterNurses = () => {
    if (!searchQuery.trim()) {
      setFilteredNurses(nurses);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = nurses.filter(
      (nurse) =>
        nurse.name?.toLowerCase().includes(query) ||
        nurse.email?.toLowerCase().includes(query) ||
        nurse.phoneNumber?.includes(query)
    );
    setFilteredNurses(filtered);
  };

  const handleAssignClick = (nurse) => {
    setSelectedNurse(nurse);
    setShowAssignModal(true);
  };

  const handleAssignSuccess = () => {
    setShowAssignModal(false);
    setSelectedNurse(null);
    fetchNurseAssignments();
  };

  const handleDeleteClick = async (assignmentId, nurseName, doctorName) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove the assignment of ${nurseName} from Dr. ${doctorName}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log("Deleting assignment:", assignmentId);
      const response = await axiosInstance.delete(
        `/nurse-assignments/${assignmentId}`
      );
      console.log("Delete response:", response.data);

      // Refresh the list
      await fetchNurseAssignments();
      alert("Assignment removed successfully!");
    } catch (error) {
      console.error("Failed to delete assignment:", error);
      console.error("Error response:", error.response?.data);
      alert(
        error.response?.data?.message ||
          "Failed to delete assignment. Please try again."
      );
    }
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Nurse-Doctor Assignments"
        subtitle="Manage nurse assignments to doctors"
        showBackButton={true}
      />

      {/* Search */}
      <div className="mt-6 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 mt-10">Loading...</div>
      ) : filteredNurses.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {searchQuery
            ? "No nurses found matching your search."
            : "No nurses available."}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredNurses.map((nurse) => (
            <div
              key={nurse.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition"
            >
              {/* Nurse Info */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {nurse.name}
                </h3>
                <p className="text-sm text-gray-600">{nurse.email}</p>
                <p className="text-sm text-gray-600">{nurse.phoneNumber}</p>
              </div>

              {/* Assigned Doctors */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Assigned Doctors:
                </h4>
                {nurse.assignments && nurse.assignments.length > 0 ? (
                  <div className="space-y-2">
                    {nurse.assignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg"
                      >
                        <span className="text-sm font-medium text-blue-800">
                          Dr. {assignment.doctor?.name}
                        </span>
                        <button
                          onClick={() =>
                            handleDeleteClick(
                              assignment.id,
                              nurse.name,
                              assignment.doctor?.name
                            )
                          }
                          className="text-red-600 hover:text-red-800 transition"
                          title="Remove assignment"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No doctors assigned
                  </p>
                )}
              </div>

              {/* Assign Button */}
              <button
                onClick={() => handleAssignClick(nurse)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <UserPlus className="w-4 h-4" />
                <span>Assign to Doctor</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <AssignNurseModal
          nurse={selectedNurse}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedNurse(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
};

export default NurseAssignments;
